import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, type ListRef } from '@banhala/ui-kit';
import type {
  AssembleFilterGoodsListProps,
  AssembleReviewCardItem,
} from './types';
import { useAssembleFetch } from './useAssembleFetch';
import { ReviewCard } from './components/ReviewCard';
import { RadioFilterChips } from './components/RadioFilterChips';
import { CoordinatedItemsHeader } from './components/CoordinatedItemsHeader';
import { GoodsCarousel } from '../GoodsCarousel';
import type { Entity } from '../GoodsCarousel/types';

/**
 * ASSEMBLE_FILTER_GOODS_LIST 아토믹 컴포넌트
 *
 * - 서버가 탭별 URL N개를 내려주면 병렬 prefetch → 캐시
 * - 탭 전환 시 캐시에서 즉시 교체 (네트워크 없음)
 * - RadioFilter ↔ ReviewCard 양방향 동기화
 *
 * iOS MetaFeedAssembleFilterGoodsListCell 대응
 */
export const AssembleFilterGoodsList = ({
  body,
  likeMap,
  setLikeMap,
  screen,
  screenName,
  logging,
  logDisabled,
}: AssembleFilterGoodsListProps) => {
  const { item } = body;
  const filterItems = item.items ?? [];
  const radioFilter = item.radio;
  const reviewCardList = item.review_card_list;
  const carouselDescription = item.carousel_description;

  // --- 병렬 fetch + 캐시 (찜 동기화 포함) ---
  const { cachedTabs, validQueryValues, isLoading } =
    useAssembleFetch(filterItems, setLikeMap);

  // --- 유효한 필터/리뷰만 필터링 (실패 탭 제거) ---
  const filteredRadioItems = useMemo(() => {
    if (!radioFilter?.possible_items) return [];
    return radioFilter.possible_items.filter(item =>
      validQueryValues.has(item.query_value),
    );
  }, [radioFilter?.possible_items, validQueryValues]);

  const filteredReviewItems = useMemo(() => {
    if (!reviewCardList?.items) return [];
    return reviewCardList.items.filter(item =>
      validQueryValues.has(item.query_value ?? ''),
    );
  }, [reviewCardList?.items, validQueryValues]);

  // --- 선택 상태 ---
  const defaultValue =
    radioFilter?.default_value ??
    radioFilter?.selected_value ??
    filteredRadioItems[0]?.query_value ??
    '';
  const [selectedQueryValue, setSelectedQueryValue] = useState(defaultValue);

  // defaultValue가 실패 탭이면 첫 번째 유효 탭으로 변경
  useEffect(() => {
    if (
      !isLoading &&
      validQueryValues.size > 0 &&
      !validQueryValues.has(selectedQueryValue)
    ) {
      const firstValid = filteredRadioItems[0]?.query_value;
      if (firstValid) setSelectedQueryValue(firstValid);
    }
  }, [isLoading, validQueryValues, selectedQueryValue, filteredRadioItems]);

  // --- 양방향 동기화 ---
  const reviewListRef = useRef<ListRef<AssembleReviewCardItem>>(null);
  const isSyncingRef = useRef(false);

  // 탭 클릭 → 리뷰카드 스크롤
  // isSyncingRef: 칩 클릭으로 리뷰 스크롤 시 onViewableItemsChanged 콜백 무시용
  const handleRadioSelect = useCallback(
    (queryValue: string) => {
      isSyncingRef.current = true;

      setSelectedQueryValue(queryValue);

      const reviewIndex = filteredReviewItems.findIndex(
        r => r.query_value === queryValue,
      );
      if (reviewIndex >= 0) {
        reviewListRef.current?.scrollToIndex({
          index: reviewIndex,
          animated: false,
        });
      }

      // 즉시 스크롤이므로 다음 프레임에서 가드 해제
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    },
    [filteredReviewItems],
  );

  // 리뷰카드 스와이프 → 탭 선택
  // 스와이프 경로에서는 isSyncingRef 가드 불필요 — 항상 최신 값 반영
  const handleReviewPageChange = useCallback(
    (index: number) => {
      if (isSyncingRef.current) return; // 칩 클릭에 의한 스크롤 중이면 무시

      const reviewItem = filteredReviewItems[index];
      if (reviewItem?.query_value) {
        setSelectedQueryValue(reviewItem.query_value);
      }
    },
    [filteredReviewItems],
  );

  // --- 현재 탭 상품 데이터 ---
  const currentTabData = cachedTabs.get(selectedQueryValue);
  const currentGoodsEntity = useMemo(() => {
    if (!currentTabData) return null;
    return currentTabData.items as Entity[];
  }, [currentTabData]);

  const selectedReviewIndex = useMemo(
    () =>
      Math.max(
        0,
        filteredReviewItems.findIndex(
          r => r.query_value === selectedQueryValue,
        ),
      ),
    [filteredReviewItems, selectedQueryValue],
  );

  // --- 로딩 중이거나 모든 탭 실패 시 렌더 안 함 ---
  if (isLoading || validQueryValues.size === 0) return null;

  return (
    <Flex>
      {/* 1. 라디오 필터 칩 (리뷰 2개 이상일 때만 표시) */}
      <RadioFilterChips
        items={filteredRadioItems}
        selectedQueryValue={selectedQueryValue}
        onSelect={handleRadioSelect}
        logging={logging}
        logDisabled={logDisabled}
      />

      {/* 2. 리뷰 카드 */}
      {filteredReviewItems.length > 0 && (
        <ReviewCard
          items={filteredReviewItems}
          selectedIndex={selectedReviewIndex}
          onPageChange={handleReviewPageChange}
          listRef={reviewListRef}
          logging={logging}
          logDisabled={logDisabled}
        />
      )}

      {/* 3. 코디 아이템 헤더 (아이콘 + 타이틀) */}
      {carouselDescription && (
        <CoordinatedItemsHeader description={carouselDescription} />
      )}

      {/* 4. 상품 캐러셀 */}
      {currentGoodsEntity && (
        <GoodsCarousel
          entity={currentGoodsEntity}
          likeMap={likeMap}
          setLikeMap={setLikeMap}
          screen={screen}
          screenName={screenName}
          logDisabled={logDisabled}
        />
      )}
    </Flex>
  );
};
